import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  Server,
  Wrench,
  Shield,
  Clock,
  Euro,
  CheckCircle2,
  ArrowRight,
  Loader2,
  CreditCard,
  Building2,
  Phone,
  Mail,
  AlertCircle,
  Sparkles,
  Zap,
  HeadphonesIcon
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const ContractSubscriptionPage = () => {
  const { user, isClient } = useAuth();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Plans data
  const [plans, setPlans] = useState({
    hosting: [],
    maintenance: []
  });
  
  // Form state
  const [step, setStep] = useState(1); // 1: Choose plan, 2: Details, 3: Payment
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [contractType, setContractType] = useState('maintenance'); // hosting, maintenance, both
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [engagement, setEngagement] = useState('12');
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success === 'true') {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      // Fetch agency settings with plans
      const res = await api.get('/settings/agency');
      
      setPlans({
        hosting: res.data?.hosting_plans || defaultHostingPlans,
        maintenance: res.data?.maintenance_plans || defaultMaintenancePlans
      });
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlans({
        hosting: defaultHostingPlans,
        maintenance: defaultMaintenancePlans
      });
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        toast.success('Paiement confirmé ! Votre contrat est maintenant actif.');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan || !websiteUrl) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    try {
      // Create contract and initiate Stripe checkout
      const response = await api.post('/contracts/subscribe', {
        plan_id: selectedPlan.id,
        contract_type: contractType,
        website_url: websiteUrl,
        engagement_months: parseInt(engagement),
        monthly_price: selectedPlan.price
      });

      if (response.data.checkout_url) {
        // Redirect to Stripe
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Contrat créé ! Un email de confirmation vous a été envoyé.');
        navigate('/my-contracts');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la souscription');
    } finally {
      setSubmitting(false);
    }
  };

  // Default plans if not configured
  const defaultHostingPlans = [
    { id: 'hosting-basic', name: 'Basic', price: 19, features: ['5 Go stockage', 'SSL inclus', 'Support email'], recommended: false },
    { id: 'hosting-pro', name: 'Pro', price: 49, features: ['20 Go stockage', 'SSL inclus', 'Support prioritaire', 'Backup quotidien'], recommended: true },
    { id: 'hosting-enterprise', name: 'Enterprise', price: 99, features: ['100 Go stockage', 'SSL inclus', 'Support 24/7', 'Backup horaire', 'CDN inclus'], recommended: false },
  ];

  const defaultMaintenancePlans = [
    { id: 'maint-essential', name: 'Essentiel', price: 79, hours: 2, features: ['2h/mois incluses', 'Mises à jour sécurité', 'Monitoring'], recommended: false },
    { id: 'maint-confort', name: 'Confort', price: 149, hours: 4, features: ['4h/mois incluses', 'Mises à jour complètes', 'Monitoring 24/7', 'Rapport mensuel'], recommended: true },
    { id: 'maint-premium', name: 'Premium', price: 299, hours: 8, features: ['8h/mois incluses', 'Évolutions incluses', 'Support prioritaire', 'Account manager dédié'], recommended: false },
  ];

  const currentPlans = contractType === 'hosting' ? plans.hosting : plans.maintenance;

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
          <Card className="max-w-lg mx-auto text-center">
            <CardContent className="pt-8">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#1a365d] mb-2">Paiement confirmé !</h2>
              <p className="text-[#64748b] mb-6">
                Votre contrat de maintenance est maintenant actif. Vous recevrez un email de confirmation avec tous les détails.
              </p>
              <Button onClick={() => navigate('/my-contracts')} style={{ backgroundColor: theme.colors?.accent }}>
                Voir mes contrats
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1a365d] mb-2">
            Souscrire à un contrat
          </h1>
          <p className="text-[#64748b]">
            Choisissez la formule adaptée à vos besoins
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { num: 1, label: 'Formule' },
            { num: 2, label: 'Détails' },
            { num: 3, label: 'Paiement' }
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-full",
                step >= s.num ? "bg-[var(--color-accent)] text-white" : "bg-gray-100 text-[#64748b]"
              )}>
                <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                  {step > s.num ? '✓' : s.num}
                </span>
                <span className="hidden sm:inline font-medium">{s.label}</span>
              </div>
              {i < 2 && <ArrowRight className="w-4 h-4 text-[#94a3b8]" />}
            </React.Fragment>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
          </div>
        ) : (
          <>
            {/* Step 1: Choose Plan Type and Plan */}
            {step === 1 && (
              <div className="space-y-8">
                {/* Contract Type Selection */}
                <Card className="card max-w-3xl mx-auto">
                  <CardHeader>
                    <CardTitle>Type de contrat</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RadioGroup value={contractType} onValueChange={setContractType} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Label 
                        htmlFor="hosting" 
                        className={cn(
                          "flex flex-col items-center gap-3 p-6 rounded-xl border-2 cursor-pointer transition-all",
                          contractType === 'hosting' ? "border-[var(--color-accent)] bg-[var(--color-accent)]/5" : "border-[#e2e8f0] hover:border-[var(--color-accent)]/50"
                        )}
                      >
                        <RadioGroupItem value="hosting" id="hosting" className="sr-only" />
                        <Server className="w-8 h-8" style={{ color: contractType === 'hosting' ? theme.colors?.accent : '#64748b' }} />
                        <span className="font-semibold">Hébergement</span>
                        <span className="text-xs text-center text-[#64748b]">Site web hébergé et sécurisé</span>
                      </Label>
                      
                      <Label 
                        htmlFor="maintenance" 
                        className={cn(
                          "flex flex-col items-center gap-3 p-6 rounded-xl border-2 cursor-pointer transition-all",
                          contractType === 'maintenance' ? "border-[var(--color-accent)] bg-[var(--color-accent)]/5" : "border-[#e2e8f0] hover:border-[var(--color-accent)]/50"
                        )}
                      >
                        <RadioGroupItem value="maintenance" id="maintenance" className="sr-only" />
                        <Wrench className="w-8 h-8" style={{ color: contractType === 'maintenance' ? theme.colors?.accent : '#64748b' }} />
                        <span className="font-semibold">Maintenance</span>
                        <span className="text-xs text-center text-[#64748b]">Mises à jour et support technique</span>
                      </Label>
                      
                      <Label 
                        htmlFor="both" 
                        className={cn(
                          "flex flex-col items-center gap-3 p-6 rounded-xl border-2 cursor-pointer transition-all relative overflow-hidden",
                          contractType === 'both' ? "border-[var(--color-accent)] bg-[var(--color-accent)]/5" : "border-[#e2e8f0] hover:border-[var(--color-accent)]/50"
                        )}
                      >
                        <RadioGroupItem value="both" id="both" className="sr-only" />
                        <Badge className="absolute top-2 right-2 bg-orange-500 text-white text-[10px]">
                          <Sparkles className="w-3 h-3 mr-1" /> -10%
                        </Badge>
                        <Shield className="w-8 h-8" style={{ color: contractType === 'both' ? theme.colors?.accent : '#64748b' }} />
                        <span className="font-semibold">Pack Complet</span>
                        <span className="text-xs text-center text-[#64748b]">Hébergement + Maintenance</span>
                      </Label>
                    </RadioGroup>
                  </CardContent>
                </Card>

                {/* Plans Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {currentPlans.map((plan) => (
                    <Card 
                      key={plan.id || plan.name}
                      className={cn(
                        "card cursor-pointer transition-all relative overflow-hidden",
                        selectedPlan?.id === plan.id ? "ring-2 ring-[var(--color-accent)] shadow-lg" : "hover:shadow-md",
                        plan.recommended && "border-[var(--color-accent)]"
                      )}
                      onClick={() => setSelectedPlan(plan)}
                    >
                      {plan.recommended && (
                        <div className="absolute top-0 right-0 bg-[var(--color-accent)] text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                          Populaire
                        </div>
                      )}
                      
                      <CardHeader>
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                            {plan.price}€
                          </span>
                          <span className="text-[#64748b]">/mois</span>
                        </div>
                        {plan.hours && (
                          <p className="text-sm text-[#64748b]">{plan.hours}h incluses</p>
                        )}
                      </CardHeader>
                      
                      <CardContent>
                        <ul className="space-y-3">
                          {(plan.features || []).map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                      
                      <CardFooter>
                        <Button 
                          className="w-full"
                          variant={selectedPlan?.id === plan.id ? "default" : "outline"}
                          style={selectedPlan?.id === plan.id ? { backgroundColor: theme.colors?.accent } : {}}
                        >
                          {selectedPlan?.id === plan.id ? 'Sélectionné' : 'Choisir'}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>

                <div className="text-center">
                  <Button 
                    size="lg" 
                    onClick={() => setStep(2)}
                    disabled={!selectedPlan}
                    style={{ backgroundColor: theme.colors?.accent }}
                  >
                    Continuer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Contract Details */}
            {step === 2 && (
              <Card className="card max-w-2xl mx-auto">
                <CardHeader>
                  <CardTitle>Détails du contrat</CardTitle>
                  <CardDescription>Complétez les informations pour votre contrat</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Selected Plan Summary */}
                  <div className="p-4 rounded-xl bg-[#f8fafc] border border-[#e2e8f0]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#1a365d]">{selectedPlan?.name}</p>
                        <p className="text-sm text-[#64748b]">
                          {contractType === 'hosting' ? 'Hébergement' : contractType === 'maintenance' ? 'Maintenance' : 'Pack Complet'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: theme.colors?.accent }}>
                          {selectedPlan?.price}€
                        </p>
                        <p className="text-xs text-[#64748b]">par mois</p>
                      </div>
                    </div>
                  </div>

                  {/* Website URL */}
                  <div>
                    <Label htmlFor="websiteUrl">URL du site web concerné *</Label>
                    <Input
                      id="websiteUrl"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://www.monsite.fr"
                      className="mt-2"
                    />
                  </div>

                  {/* Engagement Period */}
                  <div>
                    <Label>Durée d'engagement</Label>
                    <RadioGroup value={engagement} onValueChange={setEngagement} className="grid grid-cols-3 gap-4 mt-2">
                      {[
                        { value: '6', label: '6 mois', discount: '' },
                        { value: '12', label: '12 mois', discount: '-10%' },
                        { value: '24', label: '24 mois', discount: '-20%' }
                      ].map((opt) => (
                        <Label
                          key={opt.value}
                          htmlFor={`eng-${opt.value}`}
                          className={cn(
                            "flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all",
                            engagement === opt.value ? "border-[var(--color-accent)] bg-[var(--color-accent)]/5" : "border-[#e2e8f0]"
                          )}
                        >
                          <RadioGroupItem value={opt.value} id={`eng-${opt.value}`} className="sr-only" />
                          <span className="font-semibold">{opt.label}</span>
                          {opt.discount && (
                            <Badge className="mt-1 bg-green-100 text-green-700 text-xs">{opt.discount}</Badge>
                          )}
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>

                  {/* Summary */}
                  <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Récapitulatif</p>
                        <ul className="space-y-1">
                          <li>• Premier paiement : {selectedPlan?.price}€</li>
                          <li>• Engagement : {engagement} mois</li>
                          <li>• Renouvellement automatique mensuel</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
                
                <CardFooter className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Retour
                  </Button>
                  <Button 
                    onClick={() => setStep(3)}
                    disabled={!websiteUrl}
                    style={{ backgroundColor: theme.colors?.accent }}
                  >
                    Continuer vers le paiement
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <Card className="card max-w-2xl mx-auto">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Paiement sécurisé
                  </CardTitle>
                  <CardDescription>Vous allez être redirigé vers Stripe pour finaliser votre paiement</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Order Summary */}
                  <div className="p-4 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#64748b]">Formule</span>
                      <span className="font-medium">{selectedPlan?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748b]">Type</span>
                      <span className="font-medium">
                        {contractType === 'hosting' ? 'Hébergement' : contractType === 'maintenance' ? 'Maintenance' : 'Pack Complet'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748b]">Site web</span>
                      <span className="font-medium truncate max-w-[200px]">{websiteUrl}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748b]">Engagement</span>
                      <span className="font-medium">{engagement} mois</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-semibold">Total mensuel</span>
                      <span className="text-xl font-bold" style={{ color: theme.colors?.accent }}>
                        {selectedPlan?.price}€
                      </span>
                    </div>
                  </div>

                  {/* Stripe Badge */}
                  <div className="flex items-center justify-center gap-3 p-4 bg-white rounded-xl border">
                    <Shield className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-[#64748b]">Paiement sécurisé par</span>
                    <span className="font-bold text-[#635bff]">stripe</span>
                  </div>

                  {/* Terms */}
                  <div className="flex items-start gap-3">
                    <Checkbox id="terms" />
                    <Label htmlFor="terms" className="text-sm text-[#64748b] leading-relaxed">
                      J'accepte les <a href="#" className="underline">conditions générales de vente</a> et 
                      la <a href="#" className="underline">politique de confidentialité</a>. 
                      Je comprends que mon abonnement sera renouvelé automatiquement chaque mois.
                    </Label>
                  </div>
                </CardContent>
                
                <CardFooter className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Retour
                  </Button>
                  <Button 
                    onClick={handleSubscribe}
                    disabled={submitting}
                    size="lg"
                    style={{ backgroundColor: theme.colors?.accent }}
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Payer {selectedPlan?.price}€
                  </Button>
                </CardFooter>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ContractSubscriptionPage;
